import cn from '../../../utils/cn.ts'
import { CSSProperties, useRef } from 'react'
import { IOption } from '@/types/global.ts'
import usePopup from '../../hooks/usePopup.ts'
import css from './multiDropdown.module.css'
import ArrowDown from '../../../assets/icons/arrowDown.svg?react'
import TagWithCloseIcon from '../../Tags/TagWithCloseIcon'
import Checkbox from '@/shared/Inputs/Checkbox'
import FadeInPanel from '@/shared/Animated/FadeInPanel'
import Skeleton from '@/shared/Loaders/Skeleton/index.tsx'

export type TMultiDropdown = 'filter' | 'input'

interface IProps<T extends string | number> {
  options: IOption<T>[]
  onSelect: (options: T[], name: string) => void
  selectedOptions: T[]

  panelStyle?: CSSProperties
  type: TMultiDropdown
  title?: string
  disabled?: boolean
  style?: CSSProperties
  name?: string
  loading?: boolean
}

const MultiDropdownInput = <T extends string | number>(props: IProps<T>) => {
  const ref = useRef<HTMLInputElement>()
  const { open, setOpen } = usePopup(ref)

  const handleSelect = (option: IOption<T>) => {
    if (props.selectedOptions.includes(option.value)) {
      props.onSelect(
        props.selectedOptions.filter((o) => o !== option.value),
        props.name
      )
    } else {
      props.onSelect([...props.selectedOptions, option.value], props.name)
    }
  }

  const handleRemove = (option: IOption<T>) => {
    props.onSelect(
      props.selectedOptions.filter((o) => o !== option.value),
      props.name
    )
  }

  const renderSelectedOptions = () => {
    return props.selectedOptions.map((o) => {
      const opt = props.options.find((op) => op.value === o)
      if (!opt) return null
      return (
        <TagWithCloseIcon onClose={() => handleRemove(opt)} color="gray" key={opt.value}>
          <span className="text-caption" style={{ color: 'inherit' }}>
            {opt.title}
          </span>
        </TagWithCloseIcon>
      )
    })
  }

  const renderOptions = () => {
    if (props.type === 'input') {
      return props.options
        .filter((o) => !props.selectedOptions.includes(o.value))
        .map((option, index) => (
          <span
            className={cn('text-nm', css.option)}
            key={index}
            onClick={(ev) => {
              ev.stopPropagation()
              handleSelect(option)
            }}
          >
            {option.title}
          </span>
        ))
    }

    return props.options.map((option, index) => (
      <div
        data-input-type={props.type}
        className={css.option}
        key={index}
        onClick={(ev) => {
          ev.stopPropagation()
          handleSelect(option)
        }}
      >
        <Checkbox checked={props.selectedOptions.includes(option.value)} onCheck={() => handleSelect(option)} />
        <span className="text-nm">{option.title}</span>
      </div>
    ))
  }

  const renderInput = () => {
    if (props.selectedOptions.length === 0) {
      return (
        <span
          data-filled={props.selectedOptions.length > 0}
          data-input-type={props.type}
          className={cn('text-nm line-clamp', css.inputText)}
        >
          {props.title}
        </span>
      )
    }

    if (props.type === 'input') {
      return (
        <div className="inline-flex-gap gap4" style={{ flexWrap: 'wrap' }}>
          {renderSelectedOptions()}
        </div>
      )
    }

    return (
      <span
        data-filled={props.selectedOptions.length > 0}
        data-input-type={props.type}
        className={cn('text-nm line-clamp', css.inputText)}
      >
        {props.title}: Выбрано {props.selectedOptions.length}
      </span>
    )
  }

  if (props.loading) {
    return <Skeleton height="var(--input-height)" width="100%" />
  }

  return (
    <div
      className={css.wrapper}
      style={props.style}
      data-filled={props.selectedOptions.length > 0}
      data-input-type={props.type}
      data-open={open}
      ref={ref}
      onClick={!props.disabled ? () => setOpen((prev) => !prev) : null}
    >
      <div>{renderInput()}</div>

      {!props.disabled && (
        <ArrowDown
          data-open={open}
          data-filled={props.selectedOptions.length > 0}
          data-input-type={props.type}
          className={cn(css.arrow)}
        />
      )}
      <FadeInPanel open={open} className={css.options} style={props.panelStyle}>
        {renderOptions()}
      </FadeInPanel>
    </div>
  )
}

export default MultiDropdownInput
